import type { ReactNode } from "react";
import type { ListCellContext, WidgetDescriptor, WidgetSlot } from "./types";
import type { ListColumn } from "../core";

function defaultRender(value: ListCellContext["value"]): ReactNode {
    if (value == null || value === "") return <span className="cell-muted">—</span>;
    return <span>{String(value)}</span>;
}

// Central widget registry. Built-ins register themselves once at module-load
// time (see ./builtins.tsx); domains can extend with `registerWidget()` from
// their own bootstrap. Two-axis lookup:
//
//   1. By widget KEY    (column.widget="avatar_title" → "avatar_title" entry)
//   2. By field TYPE    (no widget, field.type="boolean" → "type:boolean" entry)
//   3. Fallback default — plain String(value) cell.
//
// Singleton because every consumer (ListView, FormView read-mode, KanbanView
// card render) shares the same surface; registering twice is a programmer
// error (throw) so misconfigurations are loud.
class WidgetRegistry {
    private byKey = new Map<string, WidgetDescriptor>();
    private byType = new Map<string, WidgetDescriptor>();

    /** Register a widget addressable by `column.widget`. */
    registerWidget(key: string, descriptor: WidgetDescriptor): void {
        if (this.byKey.has(key)) {
            throw new Error(`Widget "${key}" already registered`);
        }
        this.byKey.set(key, descriptor);
    }

    /** Register a default renderer for a field TYPE (used when no widget set). */
    registerType(type: string, descriptor: WidgetDescriptor): void {
        if (this.byType.has(type)) {
            throw new Error(`Type fallback "${type}" already registered`);
        }
        this.byType.set(type, descriptor);
    }

    /** Resolution chain: widget key → type fallback → null. */
    resolve(widgetKey: string | undefined, fieldType: string | undefined): WidgetDescriptor | null {
        if (widgetKey) {
            const w = this.byKey.get(widgetKey);
            if (w) return w;
        }
        if (fieldType) {
            const w = this.byType.get(fieldType);
            if (w) return w;
        }
        return null;
    }

    /** Expand a column into its render slots. Defaults to a single `field` slot. */
    slotsFor(column: ListColumn, fieldType: string | undefined): WidgetSlot[] {
        const w = this.resolve(column.widget, fieldType);
        if (w?.slots) return w.slots(column);
        return [
            {
                kind: "field",
                column,
                widthHint: w?.listColWidthHint,
                render: (ctx) => (w?.render ? w.render(ctx) : defaultRender(ctx.value)),
            },
        ];
    }
}

// Module-scope singleton — exporting a class instance gives all consumers
// the same registry (HMR safe — the side-effect builtins re-register on
// hot-reload only if Vite re-imports builtins.tsx, which would throw on
// the duplicate-key guard. To support HMR cleanly we'd add a `clear()` —
// deferred until a domain widget hot-reloads in practice).
export const widgets = new WidgetRegistry();
